import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import getTheme from './theme/theme';
import { PrivateRoute, PublicRoute } from './routes/PrivateRoute';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './features/auth/LoginPage';
import RegisterPage from './features/auth/RegisterPage';
import DashboardPage from './features/dashboard/DashboardPage';
import POSPage from './components/pos/POSPage';
import ProductsPage from './features/products/ProductsPage';
import ReportsPage from './features/reports/ReportsPage';
import CategoriesPage from './features/categories/CategoriesPage';
import InventoryPage from './features/inventory/InventoryPage';
import PurchasesPage from './features/purchases/PurchasesPage';
import CustomersPage from './features/customers/CustomersPage';
import SuppliersPage from './features/suppliers/SuppliersPage';
import SalesPage from './features/sales/SalesPage';
import ExpensesPage from './features/expenses/ExpensesPage';
import LedgerPage from './features/ledger/LedgerPage';
import UsersPage from './features/users/UsersPage';
import SettingsPage from './features/settings/SettingsPage';
import BrandsPage from './features/products/BrandsPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import SuperAdminLayout from './features/superadmin/SuperAdminLayout';
import SuperAdminLogin from './features/superadmin/SuperAdminLogin';
import SuperAdminDashboard from './features/superadmin/SuperAdminDashboard';
import TenantsPage from './features/superadmin/TenantsPage';
import TicketsPage from './features/superadmin/TicketsPage';
import SubscriptionPage from './features/settings/SubscriptionPage';
import StockMovementsPage from './features/inventory/StockMovementsPage';
import BranchesPage from './features/branches/BranchesPage';
import ProductionPage from './features/production/ProductionPage';
import MedicalPage from './features/medical/MedicalPage';
import TenantTicketsPage from './features/support/TicketsPage';
import ProfilePage from './features/profile/ProfilePage';
import PlansPage from './features/superadmin/PlansPage';
import BillingPage from './features/superadmin/BillingPage';
import DomainsPage from './features/superadmin/DomainsPage';
import UsagePage from './features/superadmin/UsagePage';
import AuditLogsPage from './features/superadmin/AuditLogsPage';
import NotificationsPage from './features/superadmin/NotificationsPage';
import SaasSettingsPage from './features/superadmin/SaasSettingsPage';
import BarcodeLabelPage from './features/products/BarcodeLabelPage';
import ShiftsPage from './features/shifts/ShiftsPage';
import QuotationsPage from './features/quotations/QuotationsPage';
import EODReportPage from './features/reports/EODReportPage';
import CashFlowPage from './features/reports/CashFlowPage';
import VouchersPage from './features/vouchers/VouchersPage';
import StocktakePage from './features/inventory/StocktakePage';
import CustomerDetailPage from './features/customers/CustomerDetailPage';
import ExpiryPage from './features/inventory/ExpiryPage';
import ReorderPage from './features/inventory/ReorderPage';
import GSTReportPage from './features/reports/GSTReportPage';
import SupplierDetailPage from './features/suppliers/SupplierDetailPage';
import StaffReportPage from './features/reports/StaffReportPage';
import MarginsReportPage from './features/reports/MarginsReportPage';
import AgingReportPage from './features/reports/AgingReportPage';
import DamagePage from './features/inventory/DamagePage';
import './utils/i18n';

const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'muiltr' });

function App() {
  const { darkMode, language } = useSelector(s => s.ui);
  const theme = useMemo(() => getTheme(darkMode ? 'dark' : 'light', language), [darkMode, language]);
  const cache = language === 'ur' ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* Private */}
            <Route element={<PrivateRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/pos" element={<POSPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/sales" element={<SalesPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/purchases" element={<PurchasesPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/ledger" element={<LedgerPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/profile" element={<SettingsPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/settings/subscription" element={<SubscriptionPage />} />
                <Route path="/inventory/movements" element={<StockMovementsPage />} />
                <Route path="/branches" element={<BranchesPage />} />
                <Route path="/production" element={<ProductionPage />} />
                <Route path="/medical" element={<MedicalPage />} />
                <Route path="/support" element={<TenantTicketsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/barcode-labels" element={<BarcodeLabelPage />} />
                <Route path="/shifts" element={<ShiftsPage />} />
                <Route path="/quotations" element={<QuotationsPage />} />
                <Route path="/reports/eod" element={<EODReportPage />} />
                <Route path="/reports/cashflow" element={<CashFlowPage />} />
                <Route path="/vouchers" element={<VouchersPage />} />
                <Route path="/stocktake" element={<StocktakePage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
                <Route path="/inventory/expiry" element={<ExpiryPage />} />
                <Route path="/inventory/reorder" element={<ReorderPage />} />
                <Route path="/reports/gst" element={<GSTReportPage />} />
                <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                <Route path="/reports/staff" element={<StaffReportPage />} />
                <Route path="/reports/margins" element={<MarginsReportPage />} />
                <Route path="/inventory/damage" element={<DamagePage />} />
                <Route path="/reports/aging" element={<AgingReportPage />} />
              </Route>
            </Route>

            {/* Super Admin Login — public, no auth needed */}
            <Route path="/superadmin/login" element={<SuperAdminLogin />} />

            {/* Super Admin Panel — protected by SuperAdminLayout's own role guard */}
            <Route element={<SuperAdminLayout />}>
                <Route path="/superadmin" element={<SuperAdminDashboard />} />
                <Route path="/superadmin/tenants" element={<TenantsPage />} />
                <Route path="/superadmin/tickets" element={<TicketsPage />} />
                <Route path="/superadmin/plans" element={<PlansPage />} />
                <Route path="/superadmin/billing" element={<BillingPage />} />
                <Route path="/superadmin/domains" element={<DomainsPage />} />
                <Route path="/superadmin/usage" element={<UsagePage />} />
                <Route path="/superadmin/audit" element={<AuditLogsPage />} />
                <Route path="/superadmin/notifications" element={<NotificationsPage />} />
                <Route path="/superadmin/settings" element={<SaasSettingsPage />} />
              </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
